import styles from "./SettingsRules.module.scss";
import useSettingsRules from "./useSettingsRules";
import React, {useMemo} from "react";
import {
  MODE_CREATE,
  MODE_EDIT,
  RULE_TYPE_CONTAINS,
  RULE_TYPE_ENDS_WITH,
  RULE_TYPE_EQUALS,
  RULE_TYPE_STARTS_WITH
} from "../../../../codes";
import Card from "../../../../common/Card";
import CardEmpty from "../../../../common/CardEmpty";
import clsx from "clsx";
import {RuleEditor} from "../editor/RuleEditor";
import {useTranslation} from "react-i18next";
import {SortableContext, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors} from "@dnd-kit/core";
import {restrictToParentElement, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import useProject from "../../../../util/useProject";
import {FabButton} from "../../../../common/FabButton";
import {Content, ContentFixed} from "../../../../common/Content";
import {SettingsMenuMobile} from "../../Settings";
import {SettingsContent} from "../../SettingsContent";
import {SettingsTopMenu} from "../../SettingsTopMenu";

const SettingsRules = () => {
  const {runWithExpiration} = useProject();

  const {
    t,
    transactionRules,
    showRuleEditor,
    transactionCategories,
    transactionTags,
    testValue,
    updateTestValue,
    testMatchingRule,
    sortRules
  } = useSettingsRules();

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    })
  );

  return (
    <Content>
      <ContentFixed>
        <SettingsTopMenu title="header_title_settings_rules"/>

        <SettingsMenuMobile/>
      </ContentFixed>

      <SettingsContent withFab={true}>
        <FabButton onClick={runWithExpiration(() => showRuleEditor(MODE_CREATE, {}))}/>

        <Card ifCond={!transactionRules.length}>
          <CardEmpty>{t("no_transaction_rules")}</CardEmpty>
        </Card>

        <Card ifCond={!!transactionRules.length}>
          <div className={styles.ruleTester}>
            <div className={styles.ruleValue}>
              <input type="text" placeholder={t("search_placeholder")} value={testValue} onChange={updateTestValue}/>
            </div>
          </div>
        </Card>

        <Card ifCond={!!transactionRules.length}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={sortRules}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={transactionRules}
              strategy={verticalListSortingStrategy}
            >
              {transactionRules.map(transactionRule => {
                return <SortableRule
                  key={transactionRule.id}
                  transactionRule={transactionRule}
                  transactionCategories={transactionCategories}
                  transactionTags={transactionTags}
                  onClick={(transactionRule) => showRuleEditor(MODE_EDIT, transactionRule)}
                  matching={transactionRule === testMatchingRule}/>;
              })}
            </SortableContext>
          </DndContext>
        </Card>
      </SettingsContent>

      <RuleEditor/>
    </Content>
  );
};

const SortableRule = ({transactionRule, transactionCategories, transactionTags, matching, onClick}) => {
  const category = transactionRule.category ? transactionCategories.find(c => c.id === transactionRule.category) : null;

  let categoryComponent = null;

  if (category) {
    const [background, color] = category.color.split("|");

    categoryComponent = <>
      <div className={styles.categoryColor} style={{background}}>
        <span className={clsx(styles.categoryIcon, "material-icons")} style={{color}}>{category.icon}</span>
      </div>

      <div className={styles.categoryName}>{category.groupName} / {category.name}</div>
    </>;
  }

  const {t} = useTranslation("settings_rules");

  const typeName = useMemo(() => {
    return {
      [RULE_TYPE_CONTAINS]: t("rule_type_contains"),
      [RULE_TYPE_EQUALS]: t("rule_type_equals"),
      [RULE_TYPE_STARTS_WITH]: t("rule_type_starts_with"),
      [RULE_TYPE_ENDS_WITH]: t("rule_type_ends_with")
    };
  }, [t]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    active,
    isDragging
  } = useSortable({id: transactionRule.id});

  const {runWithExpiration} = useProject();

  let background = null;

  if (isDragging) {
    background = "#eff0f2";
  } else if (active) {
    background = "#fff;";
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background,
    zIndex: isDragging ? 1000 : 0
  };

  const tags = transactionRule.tags || [];

  return <div ref={setNodeRef} style={style} className={clsx(styles.rule, matching ? styles.matching : null)}>
    <div className={styles.ruleDrag}>
      <span className="material-icons" {...attributes} {...listeners}>drag_indicator</span>
    </div>
    <div className={clsx(styles.ruleDetails, "hover-selectable")}
         onClick={runWithExpiration(() => onClick(transactionRule))}>
      <div className={styles.rulePosition}>{transactionRule.position + 1}.</div>
      <div className={styles.ruleValue}>{typeName[transactionRule.type]} "{transactionRule.value}"</div>
      <div className={styles.ruleArrow}><span className="material-icons">arrow_right_alt</span></div>
      <div className={styles.ruleResult}>
        {transactionRule.payee && <div className={styles.rulePayee}>{transactionRule.payee}</div>}

        {categoryComponent && <div className={styles.ruleCategory}>{categoryComponent}</div>}

        {!!tags.length && <div className={styles.ruleTags}>
          {tags.map(t => {
              const tag = transactionTags.find(tag => tag.name === t);

              const [background, color] = tag?.color ? tag.color.split("|") : ["#DDD", "#333"];

              return <span className={styles.tag} style={{background, color}} key={t}>
                <span className="material-icons" style={{color}}>label</span>
                {t}
              </span>;
            }
          )}
        </div>}
      </div>
    </div>
  </div>;
};

export default SettingsRules;
